import * as React from 'react'
import {
  Box,
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
} from '@lightspeed/design-system-react'
import classNames from 'classnames'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { STEPS } from '../types'
import {
  CONTACT_OPTIONS,
  CustomerInfo,
  CUSTOMER_NAME_FIELD,
} from '../../../common/types'
import {
  getDefaultContactOption,
  hasContactInfo,
  isInvalidEmail,
} from '../../../common/customer-contact-utils'
import { ContactNumber } from '../../../common/ContactNumber'

interface Props {
  onClose: () => void
  onStepChange: (step: STEPS) => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  newCustomer: CustomerInfo | null
}

const shouldDisableNext = (customer: CustomerInfo | null) => {
  const name: string[] = []
  let hasAllNeededCustomerInfo = false
  if (customer) {
    Object.values(CUSTOMER_NAME_FIELD).forEach(option => {
      const value = customer[option]
      if (value && value.trim()) {
        name.push(option)
      }
    })
    if (hasContactInfo(customer) && name.length > 0) {
      hasAllNeededCustomerInfo = true
    }
  }

  return !hasAllNeededCustomerInfo || isInvalidEmail(customer)
}

shouldDisableNext(null)

export const Step1Add: React.FC<Props> = ({
  onStepChange,
  onClose,
  onChange,
  newCustomer,
}) => {
  return (
    <Modal onClose={onClose}>
      <StepBreadcrumbs step={STEPS.ADD_NEW_CUSTOMER} isAddingCustomer={true} />
      <ModalHeader>
        Add contact information
        <Text tag="p" className="vd-mt2">
          Add customer details to contact them about this quote.
        </Text>
      </ModalHeader>
      <ModalContent>
        <>
          <div className="vd-g-row">
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">First name</div>
              <div className="vd-value">
                <input
                  className="vd-input"
                  placeholder="Enter first name"
                  value={newCustomer!.firstName || ''}
                  onChange={onChange}
                  name={CUSTOMER_NAME_FIELD.FIRSTNAME}
                />
              </div>
            </div>
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">Last name</div>
              <div className="vd-value">
                <input
                  className="vd-input"
                  placeholder="Enter last name"
                  value={newCustomer!.lastName || ''}
                  onChange={onChange}
                  name={CUSTOMER_NAME_FIELD.LASTNAME}
                />
              </div>
            </div>
          </div>
          <div className="vd-g-row vd-mt4">
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">Contact number</div>
              <ContactNumber
                customer={newCustomer}
                onChange={onChange}
                defaultOption={getDefaultContactOption(newCustomer)}
              />
            </div>
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">Email</div>
              <div className="vd-value">
                <input
                  className={classNames('vd-input', {
                    'vd-input--error': isInvalidEmail(newCustomer),
                  })}
                  placeholder="Enter email"
                  value={newCustomer!.email || ''}
                  onChange={onChange}
                  name={CONTACT_OPTIONS.EMAIL}
                />
              </div>
            </div>
          </div>
        </>
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={() => onStepChange(STEPS.SELECT_CUSTOMER)}
            data-cy="button-back"
          >
            Back
          </Button>
          <Button
            variant="go"
            onClick={() => onStepChange(STEPS.REVIEW_DETAILS)}
            disabled={shouldDisableNext(newCustomer)}
            data-cy="button-next"
          >
            Next
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

Step1Add.displayName = 'Step1Add'
